import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Check, X } from 'lucide-react';
import { registerSchema, type RegisterInput } from '@/lib/schemas';
import { useRegister, useOAuthProviders } from '@/hooks/use-auth';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { getSafeRedirectPath, withRedirect } from '@/lib/auth-redirect';

// OAuth Icons
const DiscordIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
    </svg>
);

const GoogleIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
);

function PasswordRequirement({
    met,
    label,
}: {
    met: boolean;
    label: string;
}) {
    return (
        <div className="flex items-center gap-2 text-sm">
            {met ? (
                <Check className="h-4 w-4 text-status-success" />
            ) : (
                <X className="h-4 w-4 text-foreground-muted" />
            )}
            <span className={met ? 'text-status-success' : 'text-foreground-muted'}>
                {label}
            </span>
        </div>
    );
}

export function RegisterPage() {
    const [searchParams] = useSearchParams();
    const redirectTo = getSafeRedirectPath(searchParams.get('redirect'));
    const registerMutation = useRegister();
    const { t } = useI18n();
    const { toast } = useToast();
    const { data: oauthProviders } = useOAuthProviders();
    // R24: after a successful signup we go to the dedicated
    // /verify-email-sent page (refreshable, translated, wallpaper-aware)
    // instead of entering the app — the account is unusable until the
    // link in the e-mail is opened.
    const navigate = useNavigate();

    // Get API URL for OAuth redirects
    const API_URL = import.meta.env.VITE_API_URL || '/api';

    useEffect(() => {
        const error = searchParams.get('error');
        if (error) {
            const errorMessages: Record<string, string> = {
                'oauth_denied': t('auth.oauthDenied'),
                'invalid_request': t('auth.oauthInvalidRequest'),
                'invalid_state': t('auth.oauthInvalidState'),
                'token_exchange_failed': t('auth.oauthTokenFailed'),
                'user_fetch_failed': t('auth.oauthUserFetchFailed'),
                'email_required': t('auth.oauthEmailRequired'),
                'server_error': t('auth.oauthServerError'),
            };
            toast({
                title: t('common.error'),
                description: errorMessages[error] || t('auth.oauthServerError'),
                variant: 'destructive',
            });
            const cleanUrl = new URL(window.location.href);
            cleanUrl.searchParams.delete('error');
            window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}`);
        }
    }, [searchParams, toast, t]);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<RegisterInput>({
        resolver: zodResolver(registerSchema),
        defaultValues: {
            name: '',
            email: '',
            password: '',
        },
    });

    const password = watch('password', '');

    // Password requirements
    const requirements = {
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /[0-9]/.test(password),
    };

    const onSubmit = async (data: RegisterInput) => {
        try {
            const result = await registerMutation.mutateAsync(data);
            const email = result?.user?.email ?? data.email;
            const params = new URLSearchParams({ email });
            if (redirectTo) params.set('redirect', redirectTo);
            navigate(`/verify-email-sent?${params.toString()}`, { replace: true });
        } catch {
            // useRegister's onError surfaces the toast.
        }
    };

    const handleDiscordLogin = () => {
        window.location.href = `${API_URL}/oauth/discord?redirect=${encodeURIComponent(redirectTo)}`;
    };

    const handleGoogleLogin = () => {
        window.location.href = `${API_URL}/oauth/google?redirect=${encodeURIComponent(redirectTo)}`;
    };

    return (
        <div className="flex min-h-screen items-center justify-center p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center pb-2">
                    <CardTitle>{t('auth.createAccount')}</CardTitle>
                    <CardDescription className="pb-2">
                        {t('auth.getStarted')}
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <CardContent className="space-y-4">
                        {/* OAuth Buttons */}
                        {(oauthProviders?.discord || oauthProviders?.google) && (
                            <>
                                <div className="grid gap-2">
                                    {oauthProviders?.google && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full bg-white/10 hover:bg-white/20 border-white/20"
                                            onClick={handleGoogleLogin}
                                        >
                                            <GoogleIcon className="mr-2 h-5 w-5" />
                                            {t('auth.continueWithGoogle')}
                                        </Button>
                                    )}
                                    {oauthProviders?.discord && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full bg-[#5865F2]/10 hover:bg-[#5865F2]/20 border-[#5865F2]/30"
                                            onClick={handleDiscordLogin}
                                        >
                                            <DiscordIcon className="mr-2 h-5 w-5 text-[#5865F2]" />
                                            {t('auth.continueWithDiscord')}
                                        </Button>
                                    )}
                                </div>
                                <div className="relative my-4">
                                    <div className="absolute inset-0 flex items-center">
                                        <span className="w-full border-t" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                        <span className="bg-card px-2 text-muted-foreground">
                                            {t('auth.orContinueWith')}
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="name">{t('auth.name')}</Label>
                            <Input
                                id="name"
                                type="text"
                                placeholder={t('auth.namePlaceholder')}
                                autoComplete="name"
                                {...register('name')}
                            />
                            {errors.name && (
                                <p className="text-sm text-destructive">{errors.name.message}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email">{t('auth.email')}</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder={t('auth.emailPlaceholder')}
                                autoComplete="email"
                                {...register('email')}
                            />
                            {errors.email && (
                                <p className="text-sm text-destructive">{errors.email.message}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">{t('auth.password')}</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="••••••••"
                                autoComplete="new-password"
                                {...register('password')}
                            />
                            {errors.password && (
                                <p className="text-sm text-destructive">
                                    {errors.password.message}
                                </p>
                            )}
                            {password.length > 0 && (
                                <div className="mt-2 space-y-1 rounded-lg bg-surface-hover p-3">
                                    <PasswordRequirement
                                        met={requirements.length}
                                        label={t('auth.password8Chars')}
                                    />
                                    <PasswordRequirement
                                        met={requirements.uppercase}
                                        label={t('auth.passwordUppercase')}
                                    />
                                    <PasswordRequirement
                                        met={requirements.lowercase}
                                        label={t('auth.passwordLowercase')}
                                    />
                                    <PasswordRequirement
                                        met={requirements.number}
                                        label={t('auth.passwordNumber')}
                                    />
                                </div>
                            )}
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col gap-4">
                        <Button
                            type="submit"
                            className="w-full"
                            disabled={registerMutation.isPending}
                        >
                            {registerMutation.isPending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t('auth.creatingAccount')}
                                </>
                            ) : (
                                t('auth.createAccountBtn')
                            )}
                        </Button>
                        <p className="text-center text-sm text-foreground-muted">
                            {t('auth.haveAccount')}{' '}
                            <Link
                                to={withRedirect('/login', redirectTo)}
                                className="text-primary hover:underline font-medium"
                            >
                                {t('auth.signIn')}
                            </Link>
                        </p>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}
